import React, { useState, useEffect } from 'react';
import { Heading, CloseButton, Button, Text, Checkbox, View, Flex } from '@smarterservices/smarter-ui';
import { RadioInputGroup, RadioInput } from '@instructure/ui';
import Modal from '../../Common/ModalContained';
import InfoIcon from '@ui/shared/components/Common/InfoIcon';
import { useExamSessionStore, getStore } from '../../../stores/StoreContext';

/**
 * SendToOnboardingStepModal Component
 * 
 * Modal dialog for selecting onboarding steps to send a user back to during check-in.
 * Allows proctors to select multiple steps and choose whether to stay connected or
 * disconnect from the session after sending the user back.
 * 
 * @component
 * 
 * @description
 * This modal provides a multi-step selection interface with the following features:
 * 
 * **Eligible Steps**:
 * - Filters journey steps to only show optional onboarding steps
 * - Excludes 'WaitingForProctor' from send-back options
 * - Removes duplicate steps (by stepId)
 * - Filters out unfinished steps to prevent re-adding them
 * - Eligible steps: SpeakerCheck, IdCheck, SelfiePhoto, SecondCameraConnection, 
 *   SecondCameraPlacement, RoomScan, Rules
 * 
 * **Session Actions**:
 * - **Stay connected**: Proctor remains connected to monitor student progress
 * - **Disconnect**: Proctor disconnects, student returns to waiting queue when complete
 * 
 * **State Management**:
 * - Resets selected steps and session action on confirm or cancel
 * - Updates eligible steps when journey changes
 * - Validates at least one step is selected before confirming
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onDismiss - Callback fired when modal is dismissed/cancelled
 * @param {Function} props.onConfirm - Callback fired when user confirms selection
 * @param {Object} props.onConfirm.params - Confirmation parameters
 * @param {string[]} props.onConfirm.params.stepIds - Array of selected step IDs
 * @param {boolean} props.onConfirm.params.unfocusSession - Whether to unfocus/disconnect from session
 * 
 * @returns {JSX.Element} Modal with step selection and session action options
 * 
 * @example
 * // Basic usage
 * <SendToOnboardingStepModal
 *   open={isOpen}
 *   onDismiss={() => setIsOpen(false)}
 *   onConfirm={({ stepIds, unfocusSession }) => {
 *     console.log('Sending to steps:', stepIds);
 *     console.log('Unfocus session:', unfocusSession);
 *   }}
 * />
 */
function SendToOnboardingStepModal({ open, onDismiss, onConfirm }) {
  const [selectedSteps, setSelectedSteps] = useState([]);
  const [sessionAction, setSessionAction] = useState('stay'); // 'stay' or 'disconnect'
  const [eligibleSteps, setEligibleSteps] = useState([]);

  const journey = useExamSessionStore((state) => state.journey);
  const getUnfinishedJourneyStepIds = useExamSessionStore((state) => state.getUnfinishedJourneyStepIds);

  const LayoutStore = getStore('layoutStore');

  const optionalGoBackSteps = ['SpeakerCheck', 'IdCheck', 'SelfiePhoto', 'SecondCameraConnection', 'SecondCameraPlacement', 'RoomScan', 'Rules'];

  useEffect(() => {
    if (!journey || !journey.steps || journey.steps.length === 0) return;

    // Filter to the optional steps and ensure there is only one stepId in the array, no duplicates...
    const filteredSteps = journey.steps.filter((step) => optionalGoBackSteps.includes(step.stepId));

    // Remove duplicates by stepId - keep only the first occurrence of each stepId
    const uniqueSteps = filteredSteps.filter((step, index, self) =>
      index === self.findIndex((s) => s.stepId === step.stepId)
    );
    // now filter out by all the unfinsihed steps, b/c if there is an unfinished step, we don't want to add it again...
    const unfinishedSteps = getUnfinishedJourneyStepIds();
    const eligibleSteps = uniqueSteps.filter((step) => !unfinishedSteps.includes(step.stepId));

    setEligibleSteps(eligibleSteps);
  }, [journey, getUnfinishedJourneyStepIds]);

  const handleStepToggle = (stepId) => {
    setSelectedSteps((prev) => {
      if (prev.includes(stepId)) {
        return prev.filter((id) => id !== stepId);
      } else {
        return [...prev, stepId];
      }
    });
  };

  const handleConfirm = () => {
    if (selectedSteps.length > 0) {
      onConfirm({ stepIds: selectedSteps, unfocusSession: sessionAction === 'disconnect' });
      // Reset state
      setSelectedSteps([]);
      setSessionAction('stay');
    }
  };

  const handleCancel = () => {
    // Reset state
    setSelectedSteps([]);
    setSessionAction('stay');
    onDismiss();
  };

  return (
    <Modal
      open={open}
      onDismiss={handleCancel}
      label="Send to Onboarding Step"
      mountNode={LayoutStore.containers?.session?.element}
    >
      <Modal.Header>
        <CloseButton
          placement="end"
          offset="small"
          onClick={handleCancel}
          screenReaderLabel="Close"
        />
        <Heading>Send to Onboarding Step</Heading>
      </Modal.Header>

      <Modal.Body>
        <View as="div" width="400px">
          <View as="div" margin="none none small none">
            <Text weight="bold">Select steps to send user back to:</Text>
          </View>

          {eligibleSteps.length === 0 ? (
            <Text>No eligible steps available</Text>
          ) : (
            <View as="div" margin="none none medium none">
              {eligibleSteps.map((step) => (
                <View key={step.journeyStepId} as="div" margin="x-small none">
                  <Checkbox
                    label={step.name}
                    checked={selectedSteps.includes(step.stepId)}
                    onChange={() => handleStepToggle(step.stepId)}
                  />
                </View>
              ))}
            </View>
          )}

          <View as="div" borderWidth="small 0 0 0" padding="small none none none">
            <RadioInputGroup
              name="sessionAction"
              description="After sending student back:"
              value={sessionAction}
              onChange={(e, value) => setSessionAction(value)}
            >
              <RadioInput
                value="stay"
                label={
                  <Flex gap="x-small" alignItems="center">
                    <Text>Stay connected to session</Text>
                    <InfoIcon
                      tip="You will remain connected to this session and can continue monitoring and communicating with the student as they complete the steps you've sent them back to."
                      placement="top"
                    />
                  </Flex>
                }
              />
              <RadioInput
                value="disconnect"
                label={
                  <Flex gap="x-small" alignItems="center">
                    <Text>Disconnect from session</Text>
                    <InfoIcon
                      tip="This will disconnect you from the session, allowing you to work with other sessions. The session will not end - when the student completes the steps, they will be placed back in the waiting queue for the next available proctor."
                      placement="top"
                    />
                  </Flex>
                }
                messages={[
                  {
                    type: 'hint',
                    text: 'This will not end the session, but will disconnect you from the session allowing you to work with other sessions. When the student returns they will be placed back in the waiting queue.'
                  }
                ]}
              />
            </RadioInputGroup>
          </View>
        </View>
      </Modal.Body>

      <Modal.Footer>
        <Flex gap="small" justifyItems="end">
          <Button onClick={handleCancel}>Cancel</Button>
          {selectedSteps.length > 0 && (
            <Button
              color="primary"
              onClick={handleConfirm}
              disabled={selectedSteps.length === 0}
            >
              Send to {selectedSteps.length > 0 ? `${selectedSteps.length} Step${selectedSteps.length > 1 ? 's' : ''}` : 'Steps'}
            </Button>
          )}
        </Flex>
      </Modal.Footer>
    </Modal>
  );
}

export default SendToOnboardingStepModal;
